/**
 * GrantPipeline — /grants/pipeline kanban board (Phase G2B CP2).
 *
 * Premium-only. Free tier renders <PremiumUpgradePanel scope="pipeline" />
 * inline on the route (per CP1 §Access-control).
 *
 * Drag-and-drop:
 *   - `@dnd-kit/core` `DndContext` with `PointerSensor` + `KeyboardSensor`
 *     (accessible by default).
 *   - Column-level drop zones via `useDroppable`; card-level draggables
 *     via `useDraggable`. Sortable is NOT used — pipeline_status is a
 *     column identity, not a within-column order.
 *   - Optimistic UI on drag-end: card visually moves BEFORE the PATCH
 *     completes. On PATCH failure the card rolls back and a dismissible
 *     error banner appears at the top of the board (NOT a toast).
 *
 * Deadline urgency chips (client-computed):
 *   - `deadline_status === "open"` AND deadline within 14d → red "Due soon"
 *   - `deadline_status === "open"` AND deadline within 30d → amber "Deadline approaching"
 *   - `deadline_status === "closed"` → grey "Closed"
 *   - otherwise → no urgency chip
 *
 * Voice discipline:
 *   - Column headers use approved status names verbatim.
 *   - Every empty-state string ends without "Coming soon" / "Beta" etc.
 *   - Extended Grant disclaimer inherited from GrantShell.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useDroppable,
  useDraggable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  AlertTriangle,
  Building2,
  Calendar,
  Loader2,
  MessageSquare,
  Save,
  X,
  Target,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import GrantShell from "@/components/grants/GrantShell";
import MatchTierChip from "@/components/grants/MatchTierChip";
import PremiumUpgradePanel from "@/components/grants/PremiumUpgradePanel";
import ReadinessBadge from "@/components/documents/ReadinessBadge";
import { PageHeader } from "@/design-system/components/PageHeader";
import { Card } from "@/design-system/components/Card";

/**
 * Ordered column list — matches `PIPELINE_STATUSES_ORDERED` on the backend.
 * Human-readable labels here are compliance-critical: they map 1:1 to
 * `PipelineStatus` enum values. "Awarded" IS an approved pipeline state
 * (not a marketing claim about outcomes).
 */
const COLUMNS = [
  { key: "interested",           label: "Interested",           emptyCopy: "Grants you're considering will appear here." },
  { key: "preparing_documents",  label: "Preparing Documents",  emptyCopy: "Grants you're preparing documents for will appear here." },
  { key: "writing_application",  label: "Writing Application",  emptyCopy: "Grants you're actively drafting will appear here." },
  { key: "submitted",            label: "Submitted",            emptyCopy: "Grants you've submitted will appear here." },
  { key: "under_review",         label: "Under Review",         emptyCopy: "Grants waiting on a sponsor decision will appear here." },
  { key: "awarded",              label: "Awarded",              emptyCopy: "Grants that have been awarded will appear here." },
  { key: "declined",             label: "Declined",             emptyCopy: "Grants that were declined by the sponsor will appear here." },
];

/** Turn an ISO timestamp into "3 days ago", "just now", etc. */
function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const now = Date.now();
  const diff = Math.round((now - then) / 1000);
  if (diff < 45) return "just now";
  if (diff < 90) return "a minute ago";
  const mins = Math.round(diff / 60);
  if (mins < 45) return `${mins} minutes ago`;
  const hours = Math.round(diff / 3600);
  if (hours < 2) return "an hour ago";
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.round(diff / 86400);
  if (days < 2) return "yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.round(diff / (86400 * 30));
  if (months < 2) return "a month ago";
  if (months < 12) return `${months} months ago`;
  const years = Math.round(diff / (86400 * 365));
  return years < 2 ? "a year ago" : `${years} years ago`;
}

/** Compute urgency chip meta for a grant. Returns null when no chip. */
function urgencyFor(grant) {
  const status = grant?.deadline_status;
  if (status === "closed") {
    return { label: "Closed", color: "var(--ds-color-ink-muted)", bg: "var(--ds-color-surface-muted)" };
  }
  if (status !== "open" || !grant?.deadline) return null;
  const target = new Date(grant.deadline).getTime();
  if (Number.isNaN(target)) return null;
  const daysUntil = Math.ceil((target - Date.now()) / (1000 * 60 * 60 * 24));
  if (daysUntil < 0) return null; // truly overdue but deadline_status=open — trust backend
  if (daysUntil <= 14) {
    return { label: "Due soon", color: "var(--ds-color-danger)", bg: "var(--ds-color-danger-soft)" };
  }
  if (daysUntil <= 30) {
    return { label: "Deadline approaching", color: "var(--ds-color-warning)", bg: "var(--ds-color-warning-soft)" };
  }
  return null;
}

function UrgencyChip({ urgency }) {
  if (!urgency) return null;
  return (
    <span
      data-testid={`pipeline-urgency-${urgency.label.toLowerCase().replace(/\s+/g, "-")}`}
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]"
      style={{
        color: urgency.color,
        background: urgency.bg,
        border: `1px solid ${urgency.color}55`,
      }}
    >
      <Calendar className="w-2.5 h-2.5" />
      {urgency.label}
    </span>
  );
}

function ErrorBanner({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div
      data-testid="pipeline-error-banner"
      role="alert"
      className="mt-4 rounded-[var(--ds-radius-card)] p-3 flex items-start gap-3 border-l-4"
      style={{
        background: "var(--ds-color-danger-soft)",
        borderColor: "var(--ds-color-danger)",
        borderLeftColor: "var(--ds-color-danger)",
        color: "var(--ds-color-danger)",
      }}
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
      <p
        data-testid="pipeline-error-banner-copy"
        className="flex-1 text-[13px] leading-snug"
        style={{ color: "var(--ds-color-ink)" }}
      >
        Move failed. {message || "Please try again."}
      </p>
      <button
        type="button"
        onClick={onDismiss}
        data-testid="pipeline-error-banner-dismiss"
        className="shrink-0 rounded-[var(--ds-radius-sm)] px-2 py-1 text-[12px] hover:bg-black/5"
        aria-label="Dismiss error"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function NotesEditor({ item, onSave }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(item.notes || "");
  const [saving, setSaving] = useState(false);
  const preview = (item.notes || "").slice(0, 100);
  return (
    <div
      className="mt-3 pt-3"
      style={{ borderTop: "1px dashed var(--ds-color-border)" }}
    >
      {!open ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          data-testid={`pipeline-notes-open-${item.grant.id}`}
          className="inline-flex items-start gap-1.5 text-left text-[11px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
        >
          <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span className="line-clamp-2">
            {preview
              ? preview + (item.notes.length > 100 ? "…" : "")
              : "Add note"}
          </span>
        </button>
      ) : (
        <div onClick={(e) => e.stopPropagation()}>
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-testid={`pipeline-notes-textarea-${item.grant.id}`}
            rows={4}
            maxLength={5000}
            placeholder="e.g., Kickoff meeting Tuesday. Draft outline in Drive."
            className="w-full rounded-[var(--ds-radius-field)] px-2 py-1.5 text-xs focus:outline-none"
            style={{
              background: "var(--ds-color-surface-muted)",
              border: "1px solid var(--ds-color-border)",
              color: "var(--ds-color-ink)",
            }}
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={async () => {
                setSaving(true);
                try {
                  await onSave(value);
                  setOpen(false);
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving}
              data-testid={`pipeline-notes-save-${item.grant.id}`}
              className="inline-flex items-center gap-1 rounded-[var(--ds-radius-md)] px-3 py-1 text-[11px] font-semibold text-[var(--ds-color-accent-ink)] bg-[var(--ds-color-accent)] hover:bg-[var(--ds-color-accent-strong)] disabled:opacity-60 transition-colors"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                setValue(item.notes || "");
                setOpen(false);
              }}
              data-testid={`pipeline-notes-cancel-${item.grant.id}`}
              className="text-[11px] text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function KanbanCard({ item, onNotesSave, readiness }) {
  const navigate = useNavigate();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: item.grant.id,
    data: { pipeline_status: item.pipeline_status },
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.4 : 1,
  };
  const urgency = urgencyFor(item.grant);
  return (
    <article
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      data-testid={`pipeline-card-${item.grant.id}`}
      className="cursor-grab active:cursor-grabbing"
    >
      <div
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-border)",
          borderRadius: "var(--ds-radius-card)",
          padding: 14,
          boxShadow: "var(--ds-shadow-card)",
        }}
      >
        <div className="flex items-start justify-between gap-2">
          <p
            className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold"
            style={{ color: "var(--ds-color-ink-subtle)" }}
          >
            <Building2 className="w-3 h-3" aria-hidden="true" />
            <span className="truncate max-w-[10rem]">{item.grant.agency}</span>
          </p>
          <UrgencyChip urgency={urgency} />
        </div>
        <h3
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/grants/${item.grant.id}`);
          }}
          data-testid={`pipeline-card-title-${item.grant.id}`}
          className="mt-1 text-[14px] font-semibold leading-snug cursor-pointer hover:underline"
          style={{
            color: "var(--ds-color-ink)",
            fontFamily: "var(--ds-font-display)",
          }}
        >
          {item.grant.title}
        </h3>
        {item.match && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <MatchTierChip
              tier={item.match.match_tier}
              score={item.match.match_score}
              size="sm"
              testId={`pipeline-card-match-chip-${item.grant.id}`}
            />
            {readiness !== undefined && (
              <ReadinessBadge
                summary={readiness}
                testId={`pipeline-card-readiness-${item.grant.id}`}
              />
            )}
          </div>
        )}
        {!item.match && readiness !== undefined && (
          <div className="mt-2">
            <ReadinessBadge
              summary={readiness}
              testId={`pipeline-card-readiness-${item.grant.id}`}
            />
          </div>
        )}
        <p
          className="mt-2 text-[10px]"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          Updated {relativeTime(item.updated_at)}
        </p>
        <div onPointerDown={(e) => e.stopPropagation()}>
          <NotesEditor item={item} onSave={onNotesSave} />
        </div>
      </div>
    </article>
  );
}

function KanbanColumn({ column, items, total, capReached, onExpand, expanded, readinessByGrant }) {
  const { setNodeRef, isOver } = useDroppable({
    id: column.key,
    data: { pipeline_status: column.key },
  });
  const showExpandLink = capReached && !expanded && total > items.length;
  return (
    <section
      ref={setNodeRef}
      data-testid={`pipeline-column-${column.key}`}
      className="flex-shrink-0 w-72 rounded-[var(--ds-radius-card)] p-3 transition-colors"
      style={{
        background: isOver
          ? "var(--ds-color-accent-soft)"
          : "var(--ds-color-surface-muted)",
        border: `1px dashed ${isOver ? "var(--ds-color-accent-soft)" : "var(--ds-color-border)"}`,
        minHeight: 400,
      }}
    >
      <header className="flex items-center justify-between mb-3 px-1">
        <h2
          data-testid={`pipeline-column-title-${column.key}`}
          className="text-[13px] font-semibold"
          style={{ color: "var(--ds-color-ink)" }}
        >
          {column.label}
        </h2>
        <span
          data-testid={`pipeline-column-total-${column.key}`}
          className="text-[10px] font-semibold uppercase tracking-[0.16em]"
          style={{ color: "var(--ds-color-ink-subtle)" }}
        >
          {expanded || total <= items.length
            ? total
            : `${items.length} of ${total}`}
        </span>
      </header>
      <div className="space-y-2">
        {items.length === 0 && (
          <p
            data-testid={`pipeline-column-empty-${column.key}`}
            className="text-[11px] italic leading-snug px-1"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            {column.emptyCopy}
          </p>
        )}
        {items.map((it) => (
          <KanbanCard
            key={it.grant.id}
            item={it}
            readiness={readinessByGrant?.[it.grant.id]}
            onNotesSave={(value) =>
              // Delegated up-chain; the outer page owns the API call.
              window.__grantPipelineNotesSave(it.grant.id, value)
            }
          />
        ))}
        {showExpandLink && (
          <button
            type="button"
            onClick={() => onExpand(column.key)}
            data-testid={`pipeline-column-expand-${column.key}`}
            className="text-[11px] font-semibold hover:underline px-1"
            style={{ color: "var(--ds-color-accent)" }}
          >
            Show all ({total})
          </button>
        )}
      </div>
    </section>
  );
}

export default function GrantPipeline() {
  const [state, setState] = useState({
    loading: true,
    isPremiumUpsell: false,
    data: null,
    error: null,
  });
  const [expandedColumn, setExpandedColumn] = useState(null);
  const [banner, setBanner] = useState(null);
  // G3B CP2 — readiness batch: one fetch per pipeline load, indexed
  // by grant_id. Passed through KanbanColumn → KanbanCard so each
  // card can render its badge without an N+1 network call.
  const [readinessByGrant, setReadinessByGrant] = useState({});

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  async function load(expand = null) {
    setState((s) => ({ ...s, loading: true }));
    try {
      const params = {};
      if (expand) params.expand_column = expand;
      const res = await api.get("/grants/pipeline", { params });
      setState({
        loading: false,
        isPremiumUpsell: false,
        data: res.data,
        error: null,
      });
      // Fire-and-forget the readiness batch. Failure here does NOT
      // block the board — readiness is an ancillary surface, so we
      // just render badges as null until it lands. Never triggers
      // an error banner.
      api
        .get("/grants/pipeline/readiness")
        .then((r) => setReadinessByGrant(r.data?.items || {}))
        .catch(() => setReadinessByGrant({}));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (
        err?.response?.status === 402 &&
        detail?.code === "pipeline_premium_only"
      ) {
        setState({
          loading: false,
          isPremiumUpsell: true,
          data: null,
          error: null,
        });
        return;
      }
      setState({
        loading: false,
        isPremiumUpsell: false,
        data: null,
        error: formatApiError(err),
      });
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Global handler used by KanbanColumn → KanbanCard → NotesEditor.
  // A ref-based prop chain is cleaner but adds noise for a single hook.
  useEffect(() => {
    window.__grantPipelineNotesSave = async (grantId, value) => {
      try {
        await api.patch(`/grants/${grantId}/save`, { notes: value });
        // Update in place — mutate the local `data` copy for the affected item.
        setState((s) => {
          if (!s.data) return s;
          const columns = { ...s.data.columns };
          for (const key of Object.keys(columns)) {
            const idx = columns[key].findIndex((it) => it.grant.id === grantId);
            if (idx >= 0) {
              const item = { ...columns[key][idx], notes: value };
              columns[key] = [...columns[key]];
              columns[key][idx] = item;
              break;
            }
          }
          return { ...s, data: { ...s.data, columns } };
        });
      } catch (err) {
        setBanner(formatApiError(err) || "Please try again.");
      }
    };
    return () => {
      delete window.__grantPipelineNotesSave;
    };
  }, []);

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (!over) return;
    const grantId = active.id;
    const targetStatus = over.data?.current?.pipeline_status;
    const sourceStatus = active.data?.current?.pipeline_status;
    if (!targetStatus || targetStatus === sourceStatus) return;

    // OPTIMISTIC — move the card locally first.
    let movedItem = null;
    setState((s) => {
      if (!s.data) return s;
      const columns = { ...s.data.columns };
      const totals = { ...s.data.totals };
      const sourceIdx = columns[sourceStatus]?.findIndex(
        (it) => it.grant.id === grantId,
      );
      if (sourceIdx === -1 || sourceIdx === undefined) return s;
      movedItem = { ...columns[sourceStatus][sourceIdx], pipeline_status: targetStatus };
      columns[sourceStatus] = [...columns[sourceStatus]];
      columns[sourceStatus].splice(sourceIdx, 1);
      columns[targetStatus] = [movedItem, ...(columns[targetStatus] || [])];
      totals[sourceStatus] = Math.max(0, (totals[sourceStatus] || 0) - 1);
      totals[targetStatus] = (totals[targetStatus] || 0) + 1;
      return { ...s, data: { ...s.data, columns, totals } };
    });

    // Persist. On failure roll back visually + surface an inline banner.
    try {
      const res = await api.patch(`/grants/${grantId}/save`, {
        pipeline_status: targetStatus,
      });
      // Update updated_at in place from the server response — keeps the
      // relative-time chip accurate without a full refetch.
      const serverUpdatedAt = new Date().toISOString();
      setState((s) => {
        if (!s.data) return s;
        const columns = { ...s.data.columns };
        for (const key of Object.keys(columns)) {
          const idx = columns[key].findIndex((it) => it.grant.id === grantId);
          if (idx >= 0) {
            const item = { ...columns[key][idx], updated_at: serverUpdatedAt };
            columns[key] = [...columns[key]];
            columns[key][idx] = item;
            break;
          }
        }
        return { ...s, data: { ...s.data, columns } };
      });
      // Successful drag clears any stale error banner.
      setBanner(null);
    } catch (err) {
      // ROLLBACK — move the card back.
      setState((s) => {
        if (!s.data) return s;
        const columns = { ...s.data.columns };
        const totals = { ...s.data.totals };
        const targetIdx = columns[targetStatus]?.findIndex(
          (it) => it.grant.id === grantId,
        );
        if (targetIdx === -1 || targetIdx === undefined) return s;
        const rolledBack = { ...columns[targetStatus][targetIdx], pipeline_status: sourceStatus };
        columns[targetStatus] = [...columns[targetStatus]];
        columns[targetStatus].splice(targetIdx, 1);
        columns[sourceStatus] = [rolledBack, ...(columns[sourceStatus] || [])];
        totals[targetStatus] = Math.max(0, (totals[targetStatus] || 0) - 1);
        totals[sourceStatus] = (totals[sourceStatus] || 0) + 1;
        return { ...s, data: { ...s.data, columns, totals } };
      });
      setBanner(formatApiError(err) || "Please try again.");
    }
  }

  const totalSaved = useMemo(() => {
    if (!state.data) return 0;
    return Object.values(state.data.totals || {}).reduce((a, b) => a + b, 0);
  }, [state.data]);

  return (
    <GrantShell testId="grant-pipeline-root">
      <PageHeader
        eyebrow="Grant Navigator"
        title="Pipeline"
        description="Track every saved grant from Interested through Awarded or Declined. Drag cards between columns to update their status. Notes and status changes are educational tracking only — grant sponsors make final funding decisions."
        data-testid="grant-pipeline-header"
      />

      {state.isPremiumUpsell && <PremiumUpgradePanel scope="pipeline" />}

      {!state.isPremiumUpsell && banner && (
        <ErrorBanner message={banner} onDismiss={() => setBanner(null)} />
      )}

      {!state.isPremiumUpsell && state.loading && (
        <div
          data-testid="pipeline-loading"
          className="mt-6 flex items-center gap-2 text-[13px]"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          <Loader2 className="w-4 h-4 animate-spin" /> Loading pipeline…
        </div>
      )}

      {!state.isPremiumUpsell && !state.loading && state.error && (
        <Card
          data-testid="pipeline-error"
          className="mt-6 rounded-[var(--ds-radius-card)]"
        >
          <div className="p-6">
            <p
              className="text-[13px]"
              style={{ color: "var(--ds-color-ink)" }}
            >
              {state.error}
            </p>
          </div>
        </Card>
      )}

      {!state.isPremiumUpsell &&
        !state.loading &&
        !state.error &&
        state.data &&
        totalSaved === 0 && (
          <div
            data-testid="pipeline-empty"
            className="mt-6 rounded-[var(--ds-radius-card)] p-10 text-center"
            style={{
              background: "var(--ds-color-surface)",
              border: "1px dashed var(--ds-color-border-strong)",
            }}
          >
            <Target
              className="mx-auto w-5 h-5"
              style={{ color: "var(--ds-color-accent)" }}
              aria-hidden="true"
            />
            <p
              className="mt-3 text-[14px] font-semibold"
              style={{ color: "var(--ds-color-ink)" }}
            >
              No saved grants yet.
            </p>
            <p
              className="mt-2 text-[13px]"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              Save grants from the Grant Finder and they'll appear here in the
              Interested column, ready to move through your pipeline.
            </p>
            <Link
              to="/grants"
              data-testid="pipeline-empty-cta"
              className="mt-5 inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold transition-colors"
              style={{
                background: "var(--ds-color-accent)",
                color: "var(--ds-color-accent-ink)",
                transitionDuration: "var(--ds-duration-fast)",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "var(--ds-color-accent-strong)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "var(--ds-color-accent)")
              }
            >
              Open Grant Finder
            </Link>
          </div>
        )}

      {!state.isPremiumUpsell &&
        !state.loading &&
        !state.error &&
        state.data &&
        totalSaved > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div
              data-testid="pipeline-board"
              className="mt-6 flex gap-3 overflow-x-auto pb-6 -mx-6 px-6"
            >
              {COLUMNS.map((col) => (
                <KanbanColumn
                  key={col.key}
                  column={col}
                  items={state.data.columns[col.key] || []}
                  total={state.data.totals[col.key] || 0}
                  capReached={
                    (state.data.totals[col.key] || 0) >
                    (state.data.columns[col.key]?.length || 0)
                  }
                  expanded={expandedColumn === col.key}
                  readinessByGrant={readinessByGrant}
                  onExpand={(k) => {
                    setExpandedColumn(k);
                    load(k);
                  }}
                />
              ))}
            </div>
          </DndContext>
        )}
    </GrantShell>
  );
}
